import { Box, Grid, GridItem } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import type { SingleValue } from 'chakra-react-select';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { RiAddLine, RiDeleteBin6Line } from 'react-icons/ri';
import * as yup from 'yup';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import FaceliftInput from '@/components/Atoms/FaceliftInput';
import FaceliftSelect from '@/components/Atoms/FaceliftSelect/FaceliftSelect';
import type { SelectItem } from '@/components/Atoms/FaceliftSelect/FaceliftSelect.types';
import type { ExitConfirmationDraftAction } from '@/components/Molecules/FaceliftDialogWithExitConfirmation';
import FaceliftSuccessProgressModal from '@/components/Molecules/FaceliftSuccessProgressModal';
import Show from '@/components/Molecules/Show/Show';
import { DRAFT_TYPE_LABELS, FORM_TYPES, shippingMethodItems, statusOptions } from '@/constants';
import {
	BgProcessModule,
	useCreateBgProcess,
	useUpdateBgProcess,
} from '@/hooks/service-hooks/bgProcess';
import { useSaveDraft } from '@/hooks/service-hooks/drafts';
import {
	useGetPhysicians,
	useGetShippingLocations,
} from '@/hooks/service-hooks/contactsAccounts';
import { useGetProductsWithSizes } from '@/hooks/service-hooks/products';
import {
	useCreateBulkOrderRequest,
	useUpdateBulkOrderRequest,
} from '@/hooks/service-hooks/requests';
import { usePaginatedSelect } from '@/hooks/usePaginatedSelect';
import type { Physician, ShippingLocation } from '@/services/contactsAccounts';
import { getBulkOrderRequests } from '@/services/requests';
import { useAuthStore, useClinicStore } from '@/store';
import type { BulkOrderData } from '@/types/bulk-order';
import type { Draft, DraftFormSuccessResult } from '@/types/draft';
import type { ProductWithSizes } from '@/types/product';
import { transformBulkFormToBulkOrderRequest } from './utils';

type BulkMutationResult = BulkOrderData | null;

const extractBulkResult = (response: unknown): BulkMutationResult => {
	if (!response || typeof response !== 'object') return null;
	const data = (response as { data?: BulkOrderData }).data;
	return data ?? null;
};

const formatBulkResultMessage = (
	result: BulkMutationResult,
	action: 'create' | 'update',
	successOnly = false,
) => {
	if (!result) return '';

	if (successOnly) {
		const successVerb = action === 'create' ? 'created' : 'updated';
		return `Successfully ${successVerb} ${result.successfulOrders} order(s)!`;
	}

	const verb = action === 'create' ? 'Created' : 'Updated';
	return `${verb} ${result.successfulOrders} of ${result.totalOrders} order(s). ${result.failedOrders} failed.`;
};

const productFieldSchema = yup.object().shape({
	product: yup.mixed<SelectItem>().required('Product is required'),
	productSize: yup.string().required('Size is required'),
	productSizeLabel: yup.string().optional(), // Label for display in locked mode
	quantity: yup
		.number()
		.typeError('Quantity must be a number')
		.required('Quantity is required')
		.positive('Quantity must be positive')
		.integer('Quantity must be an integer')
		.default(1),
	orderId: yup.string().optional(),
});

const bulkOrderRequestSchema = yup.object().shape({
	clinic: yup.mixed<SelectItem>().required('Clinic is required'),
	physician: yup.mixed<SelectItem>().required('Provider is required'),
	clinicPO: yup.string().required('Clinic PO is required'),
	shipping: yup.mixed<SelectItem>().required('Shipping method is required'),
	shippingLocation: yup
		.mixed<SelectItem>()
		.required('Shipping location is required'),
	status: yup.string().default('953870000'),
	products: yup
		.array()
		.of(productFieldSchema)
		.min(1, 'At least one product is required'),
	comments: yup
		.string()
		.max(500, 'Comments must be less than 500 characters')
		.optional()
		.default(''),
});

export type BulkOrderRequestFormData = yup.InferType<
	typeof bulkOrderRequestSchema
>;

interface BulkOrderRequestFormProps {
	mode: 'create' | 'edit' | 'reorder';
	orderId?: string;
	existingDraft?: Draft;
	onSuccess?: (result?: DraftFormSuccessResult) => void;
	onIsDirtyChange?: (isDirty: boolean) => void;
	onExitConfirmationDraftActionChange?: (
		action: ExitConfirmationDraftAction | null,
	) => void;
}

const BulkOrderRequestForm: React.FC<BulkOrderRequestFormProps> = ({
	mode,
	orderId,
	existingDraft,
	onSuccess,
	onIsDirtyChange,
	onExitConfirmationDraftActionChange,
}) => {
	const {
		register,
		handleSubmit,
		formState: { errors, isDirty },
		setValue,
		watch,
		reset,
		control,
		getValues,
	} = useForm<BulkOrderRequestFormData>({
		resolver: yupResolver(bulkOrderRequestSchema) as any,
		mode: 'onSubmit',
		defaultValues: {
			clinic: undefined,
			physician: undefined,
			clinicPO: '',
			shipping: { value: '953870000', label: 'First Overnight (8:30 am)' },
			shippingLocation: undefined,
			status: '953870000',
			products: [
				{
					product: undefined,
					productSize: '',
					productSizeLabel: '',
					quantity: 0,
					orderId: undefined,
				},
			],
			comments: '',
		},
	});

	const user = useAuthStore((state) => state.user);
  const userId = user?.userInformation?._id || null;
	const userName = user?.userInformation ? `${user.userInformation.firstName} ${user.userInformation.lastName}` : '';

	const { fields, append, remove } = useFieldArray({
		control,
		name: 'products',
	});

	// Determine if we're in edit mode or reorder mode (for UI locking)
	const isEditMode = mode === 'edit';
	const isReorderMode = mode === 'reorder';
	const hasExistingBulkOrderRequest =
		Boolean(orderId) && (isEditMode || isReorderMode);
	const isUpdatingExistingBulkOrderRequest = Boolean(orderId) && isEditMode;
	const shouldLockFields = isEditMode || isReorderMode;

	// Get clinics from store
	const clinics = useClinicStore((state) => state.currentClinics);
	// React Query mutations with delayed modal close (like BenefitVerificationForm)
	const [activeMutation, setActiveMutation] = useState<
		'create' | 'update' | null
	>(null);

	// Draft save mutation
	const { mutate: saveDraft, isLoading: isSavingDraft, isEditingDraft } =
		useSaveDraft({
			existingDraft,
			onSuccess: () => {
				toast.success(
					existingDraft
						? 'Draft updated successfully'
						: 'Draft saved successfully',
				);
				onSuccess?.({ action: 'draft_saved' });
			},
		});
	
	const {
		mutate: createBgProcess,
		isLoading: isCreating,
		isSuccess: isCreateSuccess,
		isError: isCreateError,
		data: createData,
	} = useCreateBgProcess(() => {
   // Reset form only on successful create (not edit)
		if (!isEditMode) {
			reset(
				{
					clinic: undefined,
					physician: undefined,
					clinicPO: '',
					shipping: { value: '953870000', label: 'First Overnight (8:30 am)' },
					shippingLocation: undefined,
					status: '953870000',
					products: [
						{
							product: undefined,
							productSize: '',
							productSizeLabel: '',
							quantity: 1,
							orderId: undefined,
						},
					],
					comments: '',
				},
				{ keepDefaultValues: false },
			);
		}

		setTimeout(() => {
			onSuccess?.({ action: 'submitted' });
		}, 2000);
	});

	const {
		mutate: updateBgProcess,
		isLoading: isUpdating,
		isSuccess: isUpdateSuccess,
		isError: isUpdateError,
		data: updateData,
	} = useUpdateBgProcess(() => {
		setTimeout(() => {
			onSuccess?.({ action: 'submitted' });
		}, 2000);
	});

	const createResult = useMemo(
		() => extractBulkResult(createData),
		[createData],
	);
	const updateResult = useMemo(
		() => extractBulkResult(updateData),
		[updateData],
	);

	const createPartialFailure =
		isCreateSuccess && createResult?.success === false;
	const updatePartialFailure =
		isUpdateSuccess && updateResult?.success === false;

	const isLoading =
		activeMutation === 'create'
			? isCreating
			: activeMutation === 'update'
				? isUpdating
				: false;
	const hasError =
		activeMutation === 'create'
			? isCreateError || createPartialFailure
			: activeMutation === 'update'
				? isUpdateError || updatePartialFailure
				: false;
	const isMutationSuccess =
		activeMutation === 'create'
			? isCreateSuccess && !createPartialFailure
			: activeMutation === 'update'
				? isUpdateSuccess && !updatePartialFailure
				: false;

	const errorMessage = useMemo(() => {
		if (activeMutation === 'create') {
			if (isCreateError) return 'Unable to create order order. Please check your information and try again.';
			if (createPartialFailure) {
				return formatBulkResultMessage(createResult, 'create');
			}
		}
		if (activeMutation === 'update') {
			if (isUpdateError) return 'Unable to update clinic order. Please try again.';
			if (updatePartialFailure) {
				return formatBulkResultMessage(updateResult, 'update');
			}
		}
		return '';
	}, [
		activeMutation,
		createPartialFailure,
		createResult,
		isCreateError,
		isUpdateError,
		updatePartialFailure,
		updateResult,
	]);

	useEffect(() => {
		if (onIsDirtyChange) {
			// It's negative because we want to trigger the modal to show when the form is dirty
			onIsDirtyChange(!isDirty);
		}
	}, [isDirty]);

	// Load draft data
	useEffect(() => {
		if (existingDraft && !hasExistingBulkOrderRequest) {
			try {
				reset(existingDraft.formData as BulkOrderRequestFormData, { keepDefaultValues: false });
			} catch (error) {
				console.error('Failed to load draft data:', error);
			}
		}
	}, [existingDraft, hasExistingBulkOrderRequest, reset]);

	// Get success message from mutation data
	const successMessage = useMemo(() => {
		if (activeMutation === 'create' && isMutationSuccess) {
			return formatBulkResultMessage(createResult, 'create', true);
		}
		if (activeMutation === 'update' && isMutationSuccess) {
			return formatBulkResultMessage(updateResult, 'update', true);
		}
		return '';
	}, [activeMutation, createResult, isMutationSuccess, updateResult]);

	const modalOpen = isLoading || isMutationSuccess || hasError;
	const fallbackTitle =
		activeMutation === 'update'
			? 'Bulk order request updated successfully.'
			: 'Bulk order request submitted successfully.';

	// Fetch data in edit mode or reorder mode
	useEffect(() => {
		const fetchBulkOrderData = async () => {
			if (hasExistingBulkOrderRequest && orderId) {
				try {
					const response = await getBulkOrderRequests(orderId);
					const bulkData = response.data;
					const relations = bulkData._relations;

					// Extract shipping method label
					const getShippingMethodLabel = (method: number) => {
						return (
							shippingMethodItems.find((item) => item.value === String(method))
								?.label || 'First Overnight (8:30 am)'
						);
					};

					// Transform to form format
					const formData = {
						clinic: relations?.clinic
							? {
								value: relations.clinic.accountid,
								label: relations.clinic.name,
							}
							: (null as any),
						physician: relations?.doctor
							? {
								value: relations.doctor.contactid,
								label: relations.doctor.fullname,
							}
							: (null as any),
						clinicPO: bulkData.ta_clinicpo || '',
						shipping: {
							value: String(bulkData.ta_fedexshippingmethod || 953870000),
							label: getShippingMethodLabel(bulkData.ta_fedexshippingmethod),
						},
						shippingLocation: relations?.shippingLocation
							? {
								value: relations.shippingLocation.ta_shippinglocationsid,
								label: relations.shippingLocation.ta_addressline
									? `${relations.shippingLocation.ta_name} - ${relations.shippingLocation.ta_addressline}`
									: relations.shippingLocation.ta_name,
							}
							: (null as any),
						status: isReorderMode
							? '953870000'
							: String(bulkData.ta_status || '953870000'), // Always set to 'Open' for reorder
						products:
							bulkData.products?.map((prod: any, index: number) => {
								const productInfo = relations?.products?.[index];
								const skuMatch = prod['ta_SKU@odata.bind']?.match(
									/ta_products\(([^)]+)\)/,
								);
								const productId = skuMatch ? skuMatch[1] : '';

								return {
									product: productInfo
										? {
											value: productInfo.ta_productid,
											label: productInfo.ta_name,
										}
										: (null as any),
									productSize: productInfo?.ta_productid || productId,
									productSizeLabel: productInfo?.ta_size || '',
									quantity: parseInt(prod.ta_quantity) || 1,
									orderId: prod.orderId,
								};
							}) || [],
						comments: bulkData.ta_comments || '',
					};

					reset(formData as any, { keepDefaultValues: false });
				} catch (error) {
					console.error('Failed to fetch bulk order data:', error);
				}
			}
		};

		fetchBulkOrderData();
	}, [hasExistingBulkOrderRequest, orderId, reset]);

	// Transform clinics from store to SelectItem format
	const clinicItems = useMemo(() => {
		return clinics.map((clinic) => ({
			value: clinic.accountid,
			label: clinic.name,
		}));
	}, [clinics]);

	// Physicians with pagination (dependent on selected clinic, disabled in edit/reorder mode)
	const physicians = usePaginatedSelect({
		queryHook: useGetPhysicians,
		queryParams: {
			clinic_id: (watch('clinic') as SelectItem)?.value || undefined,
			enabled: mode === 'create' && !!watch('clinic'), // Only load in create mode
		},
		transform: (physician: Physician) => ({
			value: physician.providerId,
			label: physician.providername,
		}),
		errorMessage: 'Unable to load providers',
		resetKey: !!watch('clinic'),
	});

	// Shipping locations with pagination (dependent on selected clinic, disabled in edit/reorder mode)
	const shippingLocations = usePaginatedSelect({
		queryHook: useGetShippingLocations,
		queryParams: {
			account_id: (watch('clinic') as SelectItem)?.value || undefined,
			enabled: mode === 'create' && !!watch('clinic'), // Only load in create mode
		},
		transform: (location: ShippingLocation) => ({
			value: location.ta_shippinglocationsid,
			label: location.ta_addressline
				? `${location.ta_name} - ${location.ta_addressline}`
				: location.ta_name,
		}),
		errorMessage: 'Unable to load shipping locations',
		resetKey: !!watch('clinic'),
	});

	// Products with sizes (dependent on selected clinic, disabled in edit/reorder mode)
	const { data: productsWithSizes, isLoading: productsLoading } =
		useGetProductsWithSizes({
			clinicId: ((watch('clinic') as SelectItem)?.value as string) || '',
			userPort: 'Woundcare+',
			enabled: mode === 'create' && !!watch('clinic'), // Only load in create mode
		});

	// Transform products to select items
	const products = useMemo(() => {
		if (!productsWithSizes?.items) return [];
		return productsWithSizes.items.map((product: ProductWithSizes) => ({
			value: product.productId,
			label: `${product.productName} (${product.qcode})`,
			qcode: product.qcode,
			sizes: product.sizes,
		}));
	}, [productsWithSizes]);

	// Get filtered products for a specific index (excluding already selected product+size combinations)
	const getFilteredProducts = (currentIndex: number): SelectItem[] => {
		if (shouldLockFields) return [];
		
		const selectedCombinations = new Set<string>();
		
		// Collect all selected product+size combinations from other rows
		formValues.products?.forEach((product: any, index: number) => {
			if (index !== currentIndex && product.product?.value && product.productSize) {
				selectedCombinations.add(`${product.product.value}-${product.productSize}`);
			}
		});
		
		// If no combinations are selected yet, return all products
		if (selectedCombinations.size === 0) return products;
		
		// Filter out products that have ALL their sizes already selected
		return products.filter((product: any) => {
			// Check if product has any available sizes left
			const availableSizes = product.sizes?.filter((size: any) => {
				return !selectedCombinations.has(`${product.value}-${size.ta_productid}`);
			});
			
			// Keep product if it has at least one available size
			return availableSizes && availableSizes.length > 0;
		});
	};

	// Get sizes for selected product (excluding already selected sizes for this product)
	const getProductSizes = (productId: string, currentIndex: number): SelectItem[] => {
		const product = productsWithSizes?.items.find(
			(p: ProductWithSizes) => p.productId === productId,
		);
		if (!product?.sizes) return [];
		
		// Collect already selected sizes for this product from other rows
		const selectedSizesForProduct = new Set<string>();
		formValues.products?.forEach((prod: any, index: number) => {
			if (
				index !== currentIndex && 
				prod.product?.value === productId && 
				prod.productSize
			) {
				selectedSizesForProduct.add(prod.productSize);
			}
		});
		
		// Filter out already selected sizes
		return product.sizes
			.filter((size) => !selectedSizesForProduct.has(size.ta_productid))
			.map((size) => ({
				value: size.ta_productid,
				label: size.ta_size,
			}));
	};

	// Reset product selections when clinic changes (only in create mode)
	useEffect(() => {
		if (shouldLockFields) return; // Skip in edit/reorder mode

		const subscription = watch((value, { name }) => {
			if (name === 'clinic') {
				// Reset all product selections
				setValue('products', [
					{
						product: null as any,
						productSize: '',
						quantity: 1,
					},
				], { shouldDirty: true });
			}
		});
		return () => subscription.unsubscribe();
	}, [watch, setValue, shouldLockFields]);

	// Handle save as draft
	const handleSaveAsDraft = useCallback(() => {
		const currentFormData = watch();
		
		// Generate title from clinic name or clinic PO
		const titleOrReference = currentFormData.clinic?.label || currentFormData.clinicPO || 'Untitled Draft';
		
		saveDraft({
			userId: userId || '',
			userName,
			formType: FORM_TYPES.ORDER_REQUEST_CLINIC,
			draftType: DRAFT_TYPE_LABELS.ORDER_REQUEST_CLINIC,
			titleOrReference,
			formData: currentFormData,
		});
	}, [saveDraft, userId, userName, watch]);

	useEffect(() => {
		if (!onExitConfirmationDraftActionChange) {
			return;
		}

		if (isEditMode || isReorderMode) {
			onExitConfirmationDraftActionChange(null);
			return;
		}

		onExitConfirmationDraftActionChange({
			label: isEditingDraft ? 'Update Draft' : 'Save as Draft',
			onClick: handleSaveAsDraft,
			loading: isSavingDraft,
			disabled: isSavingDraft || isLoading,
		});

		return () => {
			onExitConfirmationDraftActionChange(null);
		};
	}, [
		handleSaveAsDraft,
		isEditMode,
		isEditingDraft,
		isLoading,
		isReorderMode,
		isSavingDraft,
		onExitConfirmationDraftActionChange,
	]);

	const handleSubmitForm = handleSubmit(
		(formData: BulkOrderRequestFormData) => {
			if (isUpdatingExistingBulkOrderRequest) {
				// EDIT MODE - Update existing bulk orders (only quantities and comments)
				const updatePayload = {
					ta_comments: formData.comments || '',
					products:
						formData.products?.map((product: any) => ({
							orderId: product.orderId!, // Each product has its order ID
							ta_quantity: String(product.quantity || 1),
						})) || [],
				};

				setActiveMutation('update');
				updateBgProcess({
					moduleName: BgProcessModule.ORDER_REQUEST_BULK,
					userId: userId || '',
					crmId: orderId,
					clinicId: watch('clinic')?.value as string,
					payload: { 
						payload: updatePayload,
						additionalPayload: {
							clinicName: formData.clinic?.label as string,
							physicianName: formData.physician?.label as string,
						} 
					},
				})
			} else {
				// CREATE MODE or REORDER MODE - Transform form data to bulk order request payload
				// Ensure products is not undefined
				if (formData.products && formData.products.length > 0) {
					const bulkOrderPayload = transformBulkFormToBulkOrderRequest(
						formData as any,
					);
					setActiveMutation('create');
					createBgProcess({
						moduleName: BgProcessModule.ORDER_REQUEST_BULK,
						userId: userId || '',
						payload: { 
							payload: bulkOrderPayload,
							additionalPayload: {
								clinicName: formData.clinic?.label as string,
								physicianName: formData.physician?.label as string,
							} 
						},
					})
				}
			}
		},
	);

	const handleAddProduct = () => {
		append({
			product: null as any,
			productSize: '',
			quantity: 1,
		});
	};

	const handleRemoveProduct = (index: number) => {
		if (fields.length > 1) {
			remove(index);
		}
	};

	// Watch all form values for validation
	const formValues = watch();

	// Check if all required fields are filled for submit button validation
	const isFormValid = useMemo(() => {
		// Check all required top-level fields
		const hasClinic = !!formValues.clinic?.value;
		const hasPhysician = !!formValues.physician?.value;
		const hasClinicPO =
			!!formValues.clinicPO && formValues.clinicPO.trim() !== '';
		const hasShipping = !!formValues.shipping?.value;
		const hasShippingLocation = !!formValues.shippingLocation?.value;
		const hasStatus = !!formValues.status && formValues.status.trim() !== '';

		// Check if products array exists and has at least one product
		const hasProducts = formValues.products && formValues.products.length > 0;

		// Check that each product has all required fields
		const allProductsValid =
			hasProducts &&
			formValues.products!.every((product: any) => {
				return (
					!!product.product?.value &&
					!!product.productSize &&
					product.productSize.trim() !== '' &&
					product.quantity &&
					Number(product.quantity) > 0
				);
			});

		return (
			hasClinic &&
			hasPhysician &&
			hasClinicPO &&
			hasShipping &&
			hasShippingLocation &&
			hasStatus &&
			allProductsValid
		);
	}, [formValues]);

	return (
		<form onSubmit={handleSubmitForm}>
			<Box
				display="flex"
				justifyContent="center"
				px={{ base: 4, md: 6, lg: 2 }}
				py={{ base: 2, md: 2, lg: 1 }}
				pb={{ base: 2, md: 2, lg: 2 }}
			>
				<Grid
					templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
					gap={{ base: 3, md: 4, lg: 3 }}
					columnGap={{ base: 0, md: 4 }}
					w="100%"
				>
					{/* Clinic and Provider Row */}
					<GridItem colSpan={{ base: 1, md: 1 }}>
						<FaceliftSelect<SelectItem, false>
							limit={15}
							topLabel="Clinic"
							placeholder="Select Clinic"
							value={watch('clinic')}
							onChange={(newValue: SingleValue<SelectItem>) => {
								if (newValue) {
									setValue('clinic', newValue, { shouldDirty: true });
									// Reset physician and shipping location when clinic changes
									setValue('physician', null as any, { shouldDirty: true });
									setValue('shippingLocation', null as any, { shouldDirty: true });
								}
							}}
							items={shouldLockFields ? [] : clinicItems}
							loading={false}
							onLoadMore={() => {}}
							error={errors.clinic?.message as unknown as string}
							isSearchable={true}
							isDisabled={shouldLockFields}
							required
						/>
					</GridItem>
					<GridItem colSpan={{ base: 1, md: 1 }}>
						<FaceliftSelect<SelectItem, false>
							limit={15}
							topLabel="Provider"
							placeholder="Select Provider"
							value={watch('physician')}
							onChange={(newValue: SingleValue<SelectItem>) => {
								if (newValue) {
									setValue('physician', newValue, { shouldDirty: true });
								}
							}}
							items={shouldLockFields ? [] : physicians.items}
							loading={physicians.loading}
							onLoadMore={physicians.onLoadMore}
							isDisabled={shouldLockFields || !watch('clinic')}
							error={errors.physician?.message as unknown as string}
							isSearchable={true}
							required
						/>
					</GridItem>

					{/* Clinic PO and Shipping Method Row */}
					<GridItem colSpan={{ base: 1, md: 1 }}>
						<FaceliftInput
							topLabel="Clinic PO"
							placeholder="Enter Clinic PO"
							size="sm"
							{...register('clinicPO')}
							error={errors.clinicPO?.message}
							disabled={shouldLockFields}
							required
						/>
					</GridItem>
					<GridItem colSpan={{ base: 1, md: 1 }}>
						<FaceliftSelect<SelectItem, false>
							topLabel="Shipping Method"
							placeholder="Select"
							value={watch('shipping')}
							onChange={(newValue: SingleValue<SelectItem>) => {
								if (newValue) {
									setValue('shipping', newValue, { shouldDirty: true });
								}
							}}
							items={shippingMethodItems}
							error={errors.shipping?.message as unknown as string}
							isSearchable={false}
							isDisabled={shouldLockFields}
							required
						/>
					</GridItem>

					{/* Status and Shipping Location Row */}
					<GridItem colSpan={{ base: 1, md: 1 }}>
						<FaceliftSelect<SelectItem, false>
							topLabel="Status"
							placeholder="Select Status"
							value={
								statusOptions.find((s) => s.value === watch('status')) || null
							}
							onChange={(newValue: SingleValue<SelectItem>) => {
								if (newValue) {
									setValue('status', String(newValue.value), { shouldDirty: true });
								}
							}}
							items={statusOptions}
							error={errors.status?.message as unknown as string}
							isSearchable={false}
							isDisabled={shouldLockFields}
							required
						/>
					</GridItem>
					<GridItem colSpan={{ base: 1, md: 1 }}>
						<FaceliftSelect<SelectItem, false>
							limit={15}
							topLabel="Shipping Location"
							placeholder="Select Location"
							value={watch('shippingLocation')}
							onChange={(newValue: SingleValue<SelectItem>) => {
								if (newValue) {
									setValue('shippingLocation', newValue);
								}
							}}
							items={shouldLockFields ? [] : shippingLocations.items}
							loading={shippingLocations.loading}
							onLoadMore={shippingLocations.onLoadMore}
							isDisabled={shouldLockFields || !watch('clinic')}
							error={errors.shippingLocation?.message as unknown as string}
							isSearchable={true}
							required
						/>
					</GridItem>

					{/* Products Section - Full Width */}
					<GridItem colSpan={{ base: 1, md: 2 }}>
						<Box pt={4} mt={2}>
							{fields.map((field, index) => (
								<Box key={field.id} mb={3}>
									<Grid
										templateColumns={{
											base: '1fr',
											md: '1fr 1fr 1fr 45px',
										}}
										gap={3}
										alignItems="end"
									>
										<GridItem>
											<FaceliftSelect<SelectItem, false>
												topLabel={index === 0 ? 'Product Selection' : ''}
												placeholder="Select Product"
												value={watch(`products.${index}.product`)}
												onChange={(newValue: SingleValue<SelectItem>) => {
													if (newValue) {
														setValue(`products.${index}.product`, newValue, { shouldDirty: true });
														// Reset size when product changes
														setValue(`products.${index}.productSize`, '', { shouldDirty: true });
													}
												}}
												items={shouldLockFields ? [] : getFilteredProducts(index)}
												loading={productsLoading}
												isDisabled={shouldLockFields || !watch('clinic')}
												error={
													errors.products?.[index]?.product
														?.message as unknown as string
												}
												isSearchable={true}
												required
											/>
										</GridItem>
										<GridItem>
											<FaceliftSelect<SelectItem, false>
												topLabel={index === 0 ? 'Size' : ''}
												placeholder="Select Size"
												value={
													shouldLockFields &&
														watch(`products.${index}.productSize`)
														? ({
															value: watch(`products.${index}.productSize`),
															label:
																watch(`products.${index}.productSizeLabel`) ||
																watch(`products.${index}.productSize`),
														} as SelectItem)
														: watch(`products.${index}.product`)
															? getProductSizes(
																(watch(`products.${index}.product`) as any)
																	?.value,
																index,
															).find(
																(s) =>
																	s.value ===
																	watch(`products.${index}.productSize`),
															) || null
															: null
												}
												onChange={(newValue: SingleValue<SelectItem>) => {
													if (newValue) {
														setValue(
															`products.${index}.productSize`,
															String(newValue.value),
															{ shouldDirty: true }
														);
														setValue(
															`products.${index}.productSizeLabel`,
															String(newValue.label),
															{ shouldDirty: true }
														);
													}
												}}
												items={
													shouldLockFields
														? []
														: watch(`products.${index}.product`)
															? getProductSizes(
																(watch(`products.${index}.product`) as any)
																	?.value,
																index,
															)
															: []
												}
												isDisabled={
													shouldLockFields ||
													!watch(`products.${index}.product`)
												}
												error={errors.products?.[index]?.productSize?.message}
												isSearchable={true}
												required
											/>
										</GridItem>
										<GridItem>
											<Controller
												control={control}
												name={`products.${index}.quantity`}
												render={({ field }) => {
													const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
														const raw = e.target.value;
														if (raw === '') {
															field.onChange('');
															return;
														}
														const n = parseInt(raw, 10);
														if (!Number.isNaN(n) && n < 1) {
															field.onChange(1);
															return;
														}
														field.onChange(raw === '' ? '' : Number.isNaN(n) ? raw : n);
													};
													return (
														<FaceliftInput
															topLabel={index === 0 ? 'Quantity' : ''}
															placeholder="-"
															type="number"
															size="sm"
															min={1}
															value={field.value ?? ''}
															onChange={handleChange}
															onBlur={field.onBlur}
															error={errors.products?.[index]?.quantity?.message}
															disabled={false}
															required
														/>
													);
												}}
											/>
										</GridItem>
										<GridItem>
											<Show when={index > 0 && mode === 'create'}>
												<Box
													display="flex"
													alignItems="flex-end"
													justifyContent="center"
													h="40px"
												>
													<FaceliftButton
														type="button"
														onClick={() => handleRemoveProduct(index)}
														bg="transparent"
														color="red.500"
														border="1px solid"
														borderColor="red.500"
														minW="40px"
														w="40px"
														h="40px"
														p={0}
														_hover={{ bg: 'red.50' }}
														_dark={{
															_hover: {
																bg: 'whiteAlpha.100',
															},
														}}
													>
														<RiDeleteBin6Line size={12} />
													</FaceliftButton>
												</Box>
											</Show>
										</GridItem>
									</Grid>
								</Box>
							))}
							<Show when={mode === 'create'}>
								<Box mt={2}>
									<FaceliftButton
										type="button"
										onClick={handleAddProduct}
										bg="transparent"
										color="brand.600"
										border="1px solid"
										borderColor="brand.600"
										minW="140px"
										_hover={{
											bg: 'brand.50',
										}}
										_dark={{
											_hover: {
												bg: 'whiteAlpha.100',
											},
										}}
										startIcon={<RiAddLine size={20} />}
									>
										Add Product
									</FaceliftButton>
								</Box>
							</Show>
						</Box>
					</GridItem>

					{/* Comments - Full Width */}
					<GridItem colSpan={{ base: 1, md: 2 }}>
						<FaceliftInput
							topLabel="Comments/Clarifications"
							placeholder="Type Here"
							size="sm"
							{...register('comments')}
							error={errors.comments?.message}
							disabled={false} // Always enabled (editable in edit mode)
						/>
					</GridItem>

				{/* Buttons - Full Width */}
				<GridItem colSpan={{ base: 1, md: 2 }}>
					<Box 
						display="flex" 
						flexDirection={{ base: 'column', sm: 'row' }}
						gap={3} 
						justifyContent={{ base: 'center', lg: 'flex-end' }}
						mt={{ base: 0, lg: 1 }}
					>
						{!isEditMode && !isReorderMode && (
							<FaceliftButton
								minW={{ base: '100%', sm: '160px' }}
								loading={isSavingDraft}
								type="button"
								onClick={handleSaveAsDraft}
								cursor="pointer"
								disabled={isSavingDraft || isLoading}
								bg="transparent"
								color="brand.600"
								border="1px solid"
								borderColor="brand.600"
								_hover={{
									bg: 'brand.50',
								}}
								_dark={{
									_hover: {
										bg: 'whiteAlpha.100',
									},
								}}
							>
								{isEditingDraft ? 'Update Draft' : 'Save as Draft'}
							</FaceliftButton>
						)}
						<FaceliftButton
							minW={{ base: '100%', sm: '160px' }}
							loading={isLoading}
							type="submit"
							cursor="pointer"
							disabled={!isFormValid || isLoading}
						>
							{isEditMode ? 'Update' : 'Submit'}
						</FaceliftButton>
					</Box>
				</GridItem>
				</Grid>
			</Box>

			<FaceliftSuccessProgressModal
				open={modalOpen}
				globalLoading={isLoading}
				isLoading={isLoading}
				isSucceed={isMutationSuccess}
				hasError={hasError}
				errorMessage={errorMessage}
				onOpenChange={(newOpen: boolean) => {
					if (!newOpen) setActiveMutation(null);
				}}
				title={successMessage || fallbackTitle}
			/>
		</form>
	);
};

export default BulkOrderRequestForm;
